import {
    Box,
    Grid,
    Typography,
    Button,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    debounce
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import CustomAutocomplete from 'components/Inputs/CustomAutocomplete';
import CustomTextField from 'components/Inputs/CustomTextField';
import { useEffect, useState } from 'react';
import { getProjectListByProgram, getProjectOwnerList, getSectorProgramMapping } from 'service/service';
import { useSearchParams } from 'react-router-dom';
import DeleteHopConfirmationDialog from './DeleteHopConfirmationDialog';
import eventEmitter from 'SnackBarEventEmitter';
import { useTheme } from '@mui/material/styles';
import { getAutocompleteNewComponentsProps, getInputCustomCommonSx } from './DataPipelineHelper';

interface GetProjectsParams {
  programID: number, 
  idx: number, 
  filterInfo?: any, 
  currentFilterType?: string, 
  page?: number, 
  pageSize?: number, 
  stDate?: string, 
  edDate?: string, 
  sortingInfo?: any, 
  sectorId?: number, 
  projName?: string
}

export const WorkflowConfiguration = ({ hops, setHops, detailsOpen, setDetailsOpen, projectLists, setProjectLists, onValidityChange, onDataChange }: any) => {
    const theme = useTheme();
    const colors = theme.palette;
    const accordionColors = colors.dataPipeline.accordionWidget;
    const [searchParams] = useSearchParams();
    const isEdited = searchParams.get('isEdited') === 'true';
    const getHopLabel = (idx: number) => {
        const ordinals = [
            'First', 'Second', 'Third', 'Fourth', 'Fifth',
            'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'
        ];
        const ordinal = ordinals[idx] ?? `${idx + 1}th`;
        return `${ordinal} Hop`;
    };

    const disabledOutlinedInputSx = {
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${colors.inputNew.inputBorder} !important`,
        },
        '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: `${colors.inputNew.inputBorder} !important`
        },
        '& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input, & .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-input': {
            WebkitTextFillColor: `${colors.inputNew.inputPlaceholder} !important`,
            color: `${colors.inputNew.inputPlaceholder} !important`
        },
        '& .MuiFormLabel-root.MuiInputLabel-root.Mui-disabled': {
            WebkitTextFillColor: `${colors.inputNew.inputPlaceholder} !important`,
            color: `${colors.inputNew.inputPlaceholder} !important`
        },
        '& .MuiSvgIcon-root.Mui-disabled': {
            color: `${colors.inputNew.inputPlaceholder} !important`
        }
    };

    const inputOutlinedSx = {
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${colors.inputNew.inputBorder} !important`,
            borderWidth: '1.5px !important',
        },
        '& .MuiInputLabel-root': {
            color: `${colors.inputNew.labelText} !important`,
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: `${colors.inputNew.labelText} !important`,
        },
        '& .MuiInputBase-input': {
            color: `${colors.inputNew.inputText} !important`,
        }
    };

    const inputCommonSx = {
        "& .MuiInputBase-input": {
            color: `${colors.input.text} !important`,
            fontWeight: 500
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: `${colors.input.border} !important`,
        },
        "& .MuiInputLabel-root": {
            color: `${colors.input.text} !important`,
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: `${colors.input.text} !important`,
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: `${colors.input.borderFocus} !important`,
        },
        "& .MuiFormHelperText-root": {
            color: `${colors.input.text} !important`,
        },
        '& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input': {
            WebkitTextFillColor: `${colors.input.text} !important`,
        },
        '& .MuiFormLabel-root.MuiInputLabel-root.Mui-disabled': {
            WebkitTextFillColor: `${colors.input.text} !important`,
        },
    };

    const commonCustomTextFieldProps = {
        InputLabelProps: {
            sx: {
                '&&': { color: `${colors.input.text} !important` },
                '&.Mui-focused': { color: `${colors.input.text} !important` },
                '&.MuiInputLabel-shrink': { color: `${colors.input.text} !important` },
            }
        },
        InputProps: {
            sx: {
                '&&': { color: `${colors.input.text} !important` },
                '& input': { color: `${colors.input.text} !important` }
            }
        },
        sx: {
            '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: `${colors.input.border} !important` },
                '&:hover fieldset': { borderColor: `${colors.input.borderFocus} !important` },
                '&.Mui-focused fieldset': { borderColor: `${colors.input.borderFocus} !important` }
            },
           ...inputCommonSx
        }
    };

    const initialHopData = {
    hopName: '',
    sourceSystem: '',
    targetSystem: '',
    toolProcess: '',
    program: '',
    project: '',
    dependency: '',
    branchingLogic: '',
    downstreamReport: '',
    dataVolume: '',
    dataFormat: '',
    escalationpointEmailWorkflow: null,
    escalationpointContactWorkflow: '',
    smeEmail: null,
    smeContact: '',
    version: 'V2'
    };

    const [dropdownLoading, setDropdownLoading] = useState<any>({
        targetSystem: false,
        toolProcess: false,
        program: false,
        project: false,
        dependency: false,
        escalationpointEmailWorkflow: false,
        smeEmail: false,
    });

    const targetSystem: any[] = [];
    const toolProcess: any[] = [];
    const [program, setProgramList] = useState<any>([]);
    // per-hop program options so each hop can fetch and show its own V1/V2 results
    const [programsByHop, setProgramsByHop] = useState<any[][]>(() => (hops || []).map(() => []));
    const dependency: any[] = [];
    const [escalationpointEmailWorkflow, setEscalationPointEmailWorkflowList] = useState<any>([]);
    const [smeEmail, setSmeEmailList] = useState<any>([]);
    const [selectedValue, setSelectedValue] = useState("no");
    const [escalationEmailsByHop, setEscalationEmailsByHop] = useState<any[][]>(() => (hops || []).map(() => []));
    const [smeEmailsByHop, setSmeEmailsByHop] = useState<any[][]>(() => (hops || []).map(() => []));
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedHopIndex, setSelectedHopIndex] = useState<number | null>(null);
    const [ selectedHopName, setSelectedHopName] = useState<string>('');
    
    const requiredFields = isEdited 
    ? [
        'hopName',
        'program',
        'project',
        'escalationpointEmailWorkflow',
        'escalationpointContactWorkflow',
    ]
    : [
        'hopName',
        'program',
        'project',
        'escalationpointEmailWorkflow',
        'escalationpointContactWorkflow',
        'smeEmail',
        'smeContact'
    ];

    const isHopValid = (hop: any) => { 
        for (const field of requiredFields) {
            if( !hop[field] || (typeof hop[field] === 'string' && hop[field].trim() === '')) {
                return false;
            }
            if(hop.hopName?.length > 200){
                return false;
            }
        }
            return true;
    };

    // keep programsByHop length in sync with hops
    useEffect(() => {
        setProgramsByHop(prev => {
            const next = [...prev];
            while (next.length < hops.length) next.push([]);
            if (next.length > hops.length) next.splice(hops.length);
            return next;
        });
        setEscalationEmailsByHop(prev => {
            const next = [...prev];
            while (next.length < hops.length) next.push([]);
            if (next.length > hops.length) next.splice(hops.length);
            return next;
        });
        setSmeEmailsByHop(prev => {
            const next = [...prev];
            while (next.length < hops.length) next.push([]);
            if (next.length > hops.length) next.splice(hops.length);
            return next;
        });
    }, [hops.length]);

    useEffect(() => {
        getOwnerList('','escalationpointEmailWorkflow');
        getOwnerList('','smeEmail');
        getProgramData();
    }, [hops.length]);

    useEffect(() => {
        if (onValidityChange) {
            const allValid = hops.filter((h: { isDeleted?: boolean }) => !h.isDeleted).every(isHopValid);
            onValidityChange(allValid);
        }
        if (onDataChange) {
            onDataChange(hops);
        }
    }, [hops]);
    
    const handleChange = (event: any, idx: number, newValue: any = '', type: string = '') => {
    setHops((prev: any[]) => {
        const updated = [...prev];
        if (type === 'program') {
            const sourcePrograms = programsByHop[idx]?.length ? programsByHop[idx] : program;
            const selected = sourcePrograms.find((p: any) => p.Program_id === newValue?.Program_id) || null;
            updated[idx] = { ...updated[idx], program: selected, project: '' };
            setDropdownLoading((dl: any) => ({ ...dl, project: true }));
            setProjectLists((prevLists: any[][]) => {
                const newLists = [...prevLists];
                newLists[idx] = [];
                return newLists;
            });
            if (selected) {
                getProjectsByProgram({ programID: selected.Program_id, idx });
            }
        } else if (type === 'project') {
            const selected = (projectLists[idx] || []).find((p: any) => p.Project_Id === newValue?.Project_Id) || null;
            updated[idx] = { ...updated[idx], project: selected };
        } else if (['escalationpointEmailWorkflow', 'smeEmail'].includes(type)) {
            updated[idx] = { ...updated[idx], [type]: newValue || null };
        } else if (event && event.target) {
            updated[idx] = { ...updated[idx], [type]: event.target.value };
        } else if (type) {
            updated[idx] = { ...updated[idx], [type]: newValue };
        }
        return updated;
    });
    };

    const handleOnBlur = (event: any, type: string, idx: number) => {
    if (type === 'hopName') {
        setHops((prev: any[]) => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], [type]: event.target.value.trim() };
            return updated;
        });
    }
    };


    const validateAliasInput = (event: any) => {
        const allowedChars = /^[a-zA-Z0-9\s_-]*$/;
        if (!allowedChars.test(event.key)) {
            event.preventDefault();
        }
    };

    const debouncedGetOwnerList = debounce((name: string, type: string, hopIdx?: number) => {
        getOwnerList(name, type, hopIdx);
        }, 400);

    const handleInputChange = (event: any, name: any, newInputValue: any, hopIdx?: number) => {
        if ((name === 'escalationpointEmailWorkflow' || name === 'smeEmail')) {
            if (newInputValue && newInputValue.length) {
                debouncedGetOwnerList(newInputValue, name, hopIdx); // pass hopIdx
            } else if (newInputValue === '') {
                getOwnerList('', name, hopIdx); // pass hopIdx
            } else {
                if (name === 'escalationpointEmailWorkflow' && typeof hopIdx === 'number') {
                    setEscalationEmailsByHop(prev => {
                        const copy = [...prev];
                        copy[hopIdx] = [];
                        return copy;
                    });
                }
                if (name === 'smeEmail' && typeof hopIdx === 'number') {
                    setSmeEmailsByHop(prev => {
                        const copy = [...prev];
                        copy[hopIdx] = [];
                        return copy;
                    });
                }
            }
        }
    };

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedValue(event.target.value);
    };

    const getOwnerList = async (name = '', type = '', hopIdx?: number) => {
        const isPerHop = typeof hopIdx === 'number';
        const version = isPerHop ? hops[hopIdx].version : 'V2';
        setDropdownLoading({
            ...dropdownLoading,
            [type]: true
        });
        let result = await getProjectOwnerList(name, version);
        setDropdownLoading({
            ...dropdownLoading,
            [type]: false
        });

        // Deduplicate using Set
        const seen = new Set();
        let mailData: string[] = [];
        result.data.projectOwnerNames.forEach((obj: string) => {
            let formattedData = (obj.includes('null') ? obj.replace('(null)', '') : obj).trim();
            if (formattedData && !seen.has(formattedData)) {
                seen.add(formattedData);
                mailData.push(formattedData);
            }
        });

        // update per-hop list if hopIdx provided, otherwise global
        if (type === 'escalationpointEmailWorkflow') {
            if (isPerHop) {
                setEscalationEmailsByHop(prev => {
                    const copy = [...prev];
                    copy[hopIdx] = mailData;
                    return copy;
                });
            } else {
                setEscalationPointEmailWorkflowList(mailData);
            }
        }
        if (type === 'smeEmail') {
            if (isPerHop) {
                setSmeEmailsByHop(prev => {
                    const copy = [...prev];
                    copy[hopIdx] = mailData;
                    return copy;
                });
            } else {
                setSmeEmailList(mailData);
            }
        }
    };

        const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: string, idx: number) => {
            let value = event.target.value.replace(/[^0-9+-]/g, '');
            setHops((prev: any[]) => {
                const updated = [...prev];
                updated[idx] = { ...updated[idx], [field]: value };
                return updated;
            });
        };

        const getProgramData = async (idx?: number, sectorId?: number, searchKeyword?: string, version: 'V1' | 'V2' = 'V2') => {
             setDropdownLoading({
                ...dropdownLoading,
                program: true,
            });
            
            // Call sector -> program mapping API
            const resp: any = await getSectorProgramMapping(searchKeyword, version);
            setDropdownLoading({
                ...dropdownLoading,
                program: false,
            });

            const programsRaw = Object.values(resp.data || {}).flat();
            const allProgramsData = (programsRaw || []).map((program: any) => ({
                Program_id: program.Program_id || program.Program_Id || program.labels?.['label.pepwise.uuid'] || '',
                Program_Name: program.Program_Name || program.ProgramName || program.pepwise?.body?.programName || ''
            })).filter(p => p.Program_id && p.Program_Name);

            if (typeof idx === 'number') {
                setProgramsByHop(prev => {
                    const copy = [...prev];
                    copy[idx] = allProgramsData;
                    return copy;
                });
            } else {
                setProgramList(allProgramsData);
            }
        };

        const getProjectsByProgram = async ({
            programID,
            idx,
            filterInfo,
            currentFilterType,
            page,
            pageSize,
            stDate,
            edDate,
            sortingInfo,
            sectorId,
            projName
        }: GetProjectsParams) => {
            setDropdownLoading((dl: any) => ({ ...dl, project: true }));
            const version = hops[idx].version;
            let result = await getProjectListByProgram(programID, page, pageSize, currentFilterType, filterInfo, stDate, edDate, sortingInfo, sectorId, projName || '', version);
            setDropdownLoading((dl: any) => ({ ...dl, project: false }));
            const projectList = (result.data?.projectList || []).map((proj: any) => ({
                Project_Id: proj.Project_Id,
                Project_Name: proj.Project_Name,
                Project_Friendly_Name: proj.Friendly_Name || ""
            }));
            setProjectLists((prevLists: any[][]) => {
                const updated = [...prevLists];
                updated[idx] = projectList;
                return updated;
            });
        };

            const handleAddNewRow = () => {
                setHops((prev: any[]) => [...prev, { ...initialHopData }]);
                setDetailsOpen((prev: boolean[]) => [...prev, true]);
            };

            const handleAccordionToggle = (idx: number) => {
                setDetailsOpen((prev: boolean[]) => prev.map((open, i) => (i === idx ? !open : open)));
            };

            const handleDeleteClick = (idx: number) => {
                setSelectedHopIndex(idx);
                setSelectedHopName(hops[idx]?.hopName || '');
                setDeleteDialogOpen(true);
            };
            
            const handleConfirmDelete = async () => {
                if (selectedHopIndex === null) return;
                try {
                    setHops((prev: any[]) => {
                        const updated = [...prev];
                        updated[selectedHopIndex] = {
                            ...updated[selectedHopIndex],
                            isDeleted: true
                        };
                        return updated;
                    });

                    eventEmitter.emit(
                        'showSnackbar',
                        "Hop deleted successfully.",
                        'success',
                    );
                } catch (error) {
                    console.error('Failed to delete hop:', error);

                    eventEmitter.emit(
                        'showSnackbar',
                        'Failed to delete hop. Please try again.',
                        'error',
                    );
                } finally {
                    setDeleteDialogOpen(false);
                    setSelectedHopIndex(null);    
                }
            };
    return (
        <Box sx={{ mt: 3, padding: '22px', borderRadius: '8px' }}>
            <Typography variant="body2" sx={{ opacity: 0.7, mb: 4, fontStyle: 'italic', color: 'textLight.main' }}>
                ⓘ This section is to configure the workflows.
            </Typography>
            <Stack spacing={2}>
                {hops.filter((hop: any) => !hop.isDeleted)
                .map((hop:any , idx: number) => (    
                    <Accordion
                        key={`${hop}-${idx}`}
                        expanded={detailsOpen[idx]}
                        onChange={() => handleAccordionToggle(idx)}
                        sx={{
                            boxShadow: "none",
                            border: "none !important",
                            mb: 2,
                            "&:before": { display: "none" },

                            "&:not(.Mui-expanded)": {
                                backgroundColor: `${colors.accordion.bg} !important`,
                                borderRadius: "8px",
                            },
                            "&.Mui-expanded": {
                                backgroundColor: `${accordionColors.background.page} !important`,
                                borderRadius: "8px",
                                margin: "0 !important"
                            },

                            "&:not(.Mui-expanded) .MuiAccordionSummary-root": {
                                backgroundColor: `${colors.accordion.bg} !important`,
                                borderRadius: "8px",
                                border: "none !important"
                            },
                            "&.Mui-expanded .MuiAccordionSummary-root": {
                                backgroundColor: `${accordionColors.background.page} !important`,
                                borderRadius: "8px 8px 0 0",
                                border: "none !important"
                            },

                            "& .MuiAccordionDetails-root": {
                                backgroundColor: `${accordionColors.background.page} !important`,
                                borderRadius: "0 0 8px 8px",
                                border: "none !important"
                            },

                            "&, & .MuiPaper-root": {
                                boxShadow: "none !important",
                                border: "none !important",
                            },
                        }}
                    >
                        <AccordionSummary
                            aria-controls={`panel${idx}-content`}
                            id={`panel${idx}-header`}
                            sx={{
                                backgroundColor: accordionColors.background.page,
                                minHeight: '48px',
                                    position: 'relative',
                                '& .MuiAccordionSummary-content': {
                                    alignItems: 'center',
                                    margin: 0,
                                    width: "100%",
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Typography variant="subHeader"
                                    color="dark.main"
                                    sx={{}}
                                    style={{
                                        backgroundColor: 'transparent',
                                        color: accordionColors.accordionHeader.text,
                                        fontSize: '18px',
                                        fontWeight: '600'
                                    }}>
                                    {getHopLabel(idx)}
                                </Typography>
                                {detailsOpen[idx] && (
                                    <Box 
                                        sx={{
                                            flexGrow: 1,
                                            height: '1px', 
                                            backgroundColor: accordionColors.border.expandedAccordion, 
                                            mx: 2,
                                            alignSelf: "center",
                                            borderRadius: '1px' }} />
                                )}
                            </Box>
                            <Box 
                                sx={{
                                    marginLeft: 'auto' ,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <IconButton
                                    size="small"
                                    aria-label="Toggle details"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAccordionToggle(idx);
                                    }}
                                >
                                    <ExpandMoreIcon 
                                        sx={{
                                        color: detailsOpen[idx]
                                            ? accordionColors.accordionHeader.expandIconActive
                                            : accordionColors.accordionHeader.expandIconInactive,
                                        transform: detailsOpen[idx] ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease',
                                    }}/>
                                </IconButton>
                                {hops.filter((h: any) => !h.isDeleted).length > 1 && (
                                    <Tooltip title="Delete Hop">
                                        <IconButton
                                            size="small"
                                            aria-label={`Remove ${getHopLabel(idx)}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick(idx);
                                            }}
                                        >
                                            <CloseIcon sx={{ color: colors.inputNew.removeIcon }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                backgroundColor: accordionColors.background.page,
                                borderRadius: '0 0 8px 8px',
                                pt: 2,
                                // Ensure child form controls render on light background
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'transparent !important',
                                },
                                // Visible text for normal controls
                                '& .MuiInputBase-input, & .MuiSelect-select, & .MuiAutocomplete-input': {
                                    color: `${colors.inputNew.inputText} !important`,
                                },
                                // Make disabled inputs more readable
                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-input, & .MuiInputBase-input.Mui-disabled, & .MuiSelect-select.Mui-disabled, & .MuiAutocomplete-input.Mui-disabled': {
                                    WebkitTextFillColor: `${colors.inputNew.inputPlaceholder} !important`,
                                    color: `${colors.inputNew.inputPlaceholder} !important`,
                                    opacity: 1,
                                },
                                // Make disabled labels readable
                                '& .MuiInputLabel-root.Mui-disabled, & .MuiFormLabel-root.Mui-disabled, & label.Mui-disabled': {
                                    color: `${colors.inputNew.inputPlaceholder} !important`,
                                    opacity: 1,
                                },
                                // Ensure notched outline for disabled inputs is visible
                                '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                    borderColor: `${colors.inputNew.inputBorder} !important`,
                                },
                                // Make disabled radios and checkboxes visible
                                '& .MuiRadio-root.Mui-disabled, & .MuiCheckbox-root.Mui-disabled': {
                                    color: `${colors.inputNew.inputPlaceholder} !important`,
                                },
                                '& .MuiRadio-root.Mui-disabled .MuiSvgIcon-root, & .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root': {
                                    color: `${colors.inputNew.inputPlaceholder} !important`,
                                    '& path': { fill: `${colors.inputNew.inputPlaceholder} !important` }
                                },
                                // Disabled FormControlLabel text
                                '& .MuiFormControlLabel-root.Mui-disabled .MuiTypography-root': {
                                    color: `${colors.inputNew.inputPlaceholder} !important`,
                                },
                                '& .MuiInputLabel-root': {
                                    backgroundColor: 'transparent !important',
                                    padding: 0,
                                },
                                '& .MuiFormLabel-root': {
                                    color: `${colors.inputNew.labelText} !important`,
                                },
                                '& .MuiPaper-root': {
                                    backgroundColor: 'transparent !important',
                                }
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <CustomTextField
                                        id={`HopName-${idx}`}
                                        label={
                                        <Box display="flex" alignItems="center" sx={{ color: colors.inputNew.labelText }}>
                                            Hop Name *
                                            </Box>
                                        }
                                        value={hop.hopName}
                                        error={!!hop.hopName && hop.hopName.length > 200}
                                        helperText={hop.hopName.length > 200 ? "Hop Name can't contain more than 200 characters." : ''}
                                        onChange={(e) => handleChange(e, idx, '', 'hopName')}
                                        onBlur={(e) => handleOnBlur(e, 'hopName', idx)}
                                        onKeyDown={validateAliasInput}
                                        sx={inputOutlinedSx}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <CustomTextField
                                        id={`SourceSystem-${idx}`}
                                        label={
                                        <Box display="flex" alignItems="center">
                                            Source System
                                            </Box>
                                        }
                                        value={hop.sourceSystem}
                                        error={!!hop.sourceSystem && hop.sourceSystem.length > 45}
                                        helperText={hop.sourceSystem.length > 45 ? "Source System can't contain more than 45 characters." : ''}
                                        onChange={(e) => handleChange(e, idx, '', 'sourceSystem')}
                                        onBlur={(e) => handleOnBlur(e, 'sourceSystem', idx)}
                                        onKeyDown={validateAliasInput}
                                        disabled={true}
                                        sx={disabledOutlinedInputSx}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <FormControl fullWidth>
                                        <CustomAutocomplete
                                            loading={dropdownLoading.targetSystem}
                                            options={targetSystem}
                                            noOptionsText="No Data found"
                                            value={hop.targetSystem}
                                            onChange={(e, val) => handleChange(e, idx, val, 'targetSystem')}
                                            onInputChange={(e, val) => handleInputChange(e, 'targetSystem', val)}
                                            disabled={true}
                                            textFieldLabel="Target System"
                                            sx={getInputCustomCommonSx(
                                                colors,
                                                undefined,
                                                colors.DropdownV2.dropdownIconDefault
                                            )}
                                            componentsProps={getAutocompleteNewComponentsProps(colors)}
                                            customTextFieldProps={commonCustomTextFieldProps}
                                            popupIcon={<ExpandMoreIcon />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={4}>
                                    <FormControl fullWidth>
                                        <CustomAutocomplete
                                            loading={dropdownLoading.toolProcess}
                                            options={toolProcess}
                                            noOptionsText="No Data found"
                                            value={hop.toolProcess}
                                            onChange={(e, val) => handleChange(e, idx, val, 'toolProcess')}
                                            onInputChange={(e, val) => handleInputChange(e, 'toolProcess', val)}
                                            disabled={true}
                                            textFieldLabel="Tool/Process"
                                            sx={getInputCustomCommonSx(
                                                colors,
                                                undefined,
                                                colors.DropdownV2.dropdownIconDefault
                                            )}
                                            componentsProps={getAutocompleteNewComponentsProps(colors)}
                                            customTextFieldProps={commonCustomTextFieldProps}
                                            popupIcon={<ExpandMoreIcon />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={4}>
                                    <FormControl fullWidth>
                                        <CustomAutocomplete
                                            loading={dropdownLoading.program}
                                            options={programsByHop[idx]?.length ? programsByHop[idx] : program}
                                            noOptionsText="No Data found"
                                            value={hop.program}
                                            onChange={(e, val) => handleChange(e, idx, val, 'program')}
                                            onInputChange={(e, val) => handleInputChange(e, 'program', val)}
                                            disabled={false}
                                            getOptionLabel={(option) => option?.Program_Name || ''}
                                            isOptionEqualToValue={(option, value) => option?.Program_id === value?.Program_id}
                                            textFieldLabel="Select Program *"
                                            sx={getInputCustomCommonSx(
                                                colors,
                                                undefined,
                                                colors.DropdownV2.dropdownIconDefault
                                            )}
                                            componentsProps={getAutocompleteNewComponentsProps(colors)}
                                            customTextFieldProps={commonCustomTextFieldProps}
                                            popupIcon={<ExpandMoreIcon />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={4}>
                                    <FormControl fullWidth>
                                        <CustomAutocomplete
                                            loading={dropdownLoading.project}
                                            options={projectLists[idx] || []}
                                            noOptionsText="No Data found"
                                            value={hop.project}
                                            onChange={(e, val) => handleChange(e, idx, val, 'project')}
                                            onInputChange={(e, val) => handleInputChange(e, 'project', val)}
                                            disabled={false}
                                            getOptionLabel={(option) => option?.Project_Name || ''}
                                            isOptionEqualToValue={(option, value) => option?.Project_Id === value?.Project_Id}
                                            textFieldLabel="Select Project *"
                                            sx={getInputCustomCommonSx(
                                                colors,
                                                undefined,
                                                colors.DropdownV2.dropdownIconDefault
                                            )}
                                            componentsProps={getAutocompleteNewComponentsProps(colors)}
                                            customTextFieldProps={commonCustomTextFieldProps}
                                            popupIcon={<ExpandMoreIcon />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={4}>
                                    <FormControl fullWidth>
                                        <CustomAutocomplete
                                            loading={dropdownLoading.dependency}
                                            options={dependency}
                                            noOptionsText="No Data found"
                                            value={hop.dependency}
                                            onChange={(e, idx, val, type) => handleChange(e, idx, val, 'dependency')}
                                            onInputChange={(e, val) => handleInputChange(e, 'dependency', val)}
                                            disabled={true}
                                            textFieldLabel="Dependency"
                                            sx={getInputCustomCommonSx(
                                                colors,
                                                undefined,
                                                colors.DropdownV2.dropdownIconDefault
                                            )}
                                            componentsProps={getAutocompleteNewComponentsProps(colors)}
                                            customTextFieldProps={commonCustomTextFieldProps}
                                            popupIcon={<ExpandMoreIcon />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={4}>
                                    <CustomTextField
                                        id={`BranchingLogic-${idx}`}
                                        label={
                                        <Box display="flex" alignItems="center">
                                            Branching Logic
                                            </Box>
                                        }
                                        value={hop.branchingLogic}
                                        error={!!hop.branchingLogic && hop.branchingLogic.length > 45}
                                        helperText={hop.branchingLogic.length > 45 ? "Branching Logic can't contain more than 45 characters." : ''}
                                        onChange={(e) => handleChange(e, idx, '', 'branchingLogic')}
                                        onBlur={(e) => handleOnBlur(e, 'branchingLogic', idx)}
                                        onKeyDown={validateAliasInput}
                                        disabled={true}
                                        sx={disabledOutlinedInputSx}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <CustomTextField
                                        id={`DownstreamReport-${idx}`}
                                        label={
                                        <Box display="flex" alignItems="center">
                                            Downstream Report/System Usage
                                            </Box>
                                        }
                                        value={hop.downstreamReport}
                                        error={!!hop.downstreamReport && hop.downstreamReport.length > 45}
                                        helperText={hop.downstreamReport.length > 45 ? "Downstream Report/System Usage can't contain more than 45 characters." : ''}
                                        onChange={(e) => handleChange(e, idx, '', 'downstreamReport')}
                                        onBlur={(e) => handleOnBlur(e, 'downstreamReport', idx)}
                                        onKeyDown={validateAliasInput}
                                        disabled={true}
                                        sx={disabledOutlinedInputSx}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <CustomTextField
                                        id={`DataVolume-${idx}`}
                                        label={
                                        <Box display="flex" alignItems="center">
                                            Data Volume
                                            </Box>
                                        }
                                        value={hop.dataVolume}
                                        error={!!hop.dataVolume && hop.dataVolume.length > 45}
                                        helperText={hop.dataVolume.length > 45 ? "Data Volume can't contain more than 45 characters." : ''}
                                        onChange={(e) => handleChange(e, idx, '', 'dataVolume')}
                                        onBlur={(e) => handleOnBlur(e, 'dataVolume', idx)}
                                        onKeyDown={validateAliasInput}
                                        disabled={true}
                                        sx={disabledOutlinedInputSx}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <CustomTextField
                                        id={`DataFormat-${idx}`}
                                        label={
                                        <Box display="flex" alignItems="center">
                                            Data Format/Structure
                                            </Box>
                                        }
                                        value={hop.dataFormat}
                                        error={!!hop.dataFormat && hop.dataFormat.length > 45}
                                        helperText={hop.dataFormat.length > 45 ? "Data Format/Structure can't contain more than 45 characters." : ''}
                                        onChange={(e) => handleChange(e, idx, '', 'dataFormat')}
                                        onBlur={(e) => handleOnBlur(e, 'dataFormat', idx)}
                                        onKeyDown={validateAliasInput}
                                        disabled={true}
                                        sx={disabledOutlinedInputSx}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} mt={2}>
                                <Grid item xs={12}>
                                    <Box sx={{
                                        position: 'relative',
                                        height: '22px',
                                        marginBottom: '5px',
                                        '&:before': {
                                            backgroundColor: accordionColors.border.expandedAccordion,
                                            content: '""',
                                            height: '1px',
                                            left: 0,
                                            position: 'absolute',
                                            top: '50%',
                                            transform: 'translateY(0)',
                                            width: '100%',
                                            zIndex: 1
                                        }
                                    }}>
                                        <Typography variant="subHeader" color="dark.main" style={{
                                                backgroundColor: accordionColors.background.page,
                                                color: accordionColors.text.link,
                                                display: 'inline-block',
                                                padding: '0 12px 0 8px',
                                                position: 'absolute',
                                                left: 0,
                                                zIndex: 2
                                            }}>Contacts</Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={4}>
                                    <FormControl fullWidth>
                                        <CustomAutocomplete
                                            loading={dropdownLoading.escalationpointEmailWorkflow}
                                            options={escalationEmailsByHop[idx]?.length ? escalationEmailsByHop[idx] : escalationpointEmailWorkflow}
                                            noOptionsText="No Data found"
                                            value={hop.escalationpointEmailWorkflow}
                                            onChange={(e, val) => handleChange(e, idx, val, 'escalationpointEmailWorkflow')}
                                            onInputChange={(e, val) => handleInputChange(e, 'escalationpointEmailWorkflow', val, idx)}
                                            disabled={false}
                                            textFieldLabel="Escalation Point *"
                                            sx={getInputCustomCommonSx(
                                                colors,
                                                undefined,
                                                colors.DropdownV2.dropdownIconDefault
                                            )}
                                            componentsProps={getAutocompleteNewComponentsProps(colors)}
                                            customTextFieldProps={commonCustomTextFieldProps}
                                            popupIcon={<ExpandMoreIcon />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={4}>
                                    <CustomTextField
                                        id={`escalationpointContact-${idx}`}
                                        label={
                                        <Box display="flex" alignItems="center" sx={{ color: colors.inputNew.labelText }}>
                                            Escalation Point Contact *
                                            </Box>
                                        }
                                        value={hop.escalationpointContactWorkflow}
                                        error={!!hop.escalationpointContactWorkflow}
                                        onChange={(e) => handlePhoneChange(e, 'escalationpointContactWorkflow', idx)}
                                        onBlur={(e) => handleOnBlur(e, 'escalationpointContactWorkflow', idx)}
                                        disabled={false}
                                        inputProps={{
                                            inputMode: 'tel',
                                            pattern: '[0-9+\\-]*'
                                        }}
                                        sx={inputOutlinedSx}
                                    />
                                </Grid>
                                <Grid item xs={4} />

                                <Grid item xs={4}>
                                    <FormControl fullWidth>
                                        <CustomAutocomplete
                                            loading={dropdownLoading.smeEmail}
                                            options={smeEmailsByHop[idx]?.length ? smeEmailsByHop[idx] : smeEmail}
                                            noOptionsText="No Data found"
                                            value={hop.smeEmail}
                                            onChange={(e, val) => handleChange(e, idx, val, 'smeEmail')}
                                            onInputChange={(e, val) => handleInputChange(e, 'smeEmail', val, idx)}
                                            disabled={false}
                                            textFieldLabel={isEdited ? 'SME (@Name/GPID/Email)' : 'SME (@Name/GPID/Email) *'}
                                            sx={getInputCustomCommonSx(
                                                colors,
                                                undefined,
                                                colors.DropdownV2.dropdownIconDefault
                                            )}
                                            componentsProps={getAutocompleteNewComponentsProps(colors)}
                                            customTextFieldProps={commonCustomTextFieldProps}
                                            popupIcon={<ExpandMoreIcon />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={4}>
                                    <CustomTextField
                                        id={`smeContact-${idx}`}
                                        label={
                                            <Box display="flex" alignItems="center" sx={{ color: colors.inputNew.labelText }}>
                                                {isEdited ? 'SME Contact' : 'SME Contact *'}
                                            </Box>
                                        }
                                        value={hop.smeContact}
                                        error={!!hop.smeContact}
                                        onChange={(e) => handlePhoneChange(e, 'smeContact', idx)}
                                        onBlur={(e) => handleOnBlur(e, 'smeContact', idx)}
                                        disabled={false}
                                        inputProps={{
                                            inputMode: 'tel',
                                            pattern: '[0-9+\\-]*'
                                        }}
                                        sx={inputOutlinedSx}
                                    />
                                </Grid>
                                <Grid item xs={4} />
                            </Grid>

                            <Grid container spacing={2} mt={2}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" color="dark.main"
                                        style={{ color: accordionColors.text.primary, fontWeight: '600', fontSize: '16px' }}>
                                        Email Summary Reports ?
                                    </Typography>
                                    <Box sx={{ backgroundColor: accordionColors.background.page, display: 'flex', marginTop: '10px' }}>
                                        <RadioGroup
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                            }}
                                            value={selectedValue}
                                            onChange={handleRadioChange}
                                        >
                                            <FormControlLabel
                                                sx={{
                                                    marginRight: '16px',
                                                    '& .MuiFormControlLabel-label': {
                                                        color: `${colors.radioGroup.optionText} !important`
                                                    },
                                                    '&.Mui-disabled .MuiFormControlLabel-label': {
                                                        color: `${colors.radioGroup.disabledBorder} !important`
                                                    }
                                                }}
                                                value="yes"
                                                control={
                                                    <Radio
                                                        sx={{
                                                            color: `${colors.radioGroup.radioBorder} !important`,
                                                            '&.Mui-checked': {
                                                                color: `${colors.radioGroup.radioCheckedBackground} !important`
                                                            },
                                                            '&.Mui-disabled': {
                                                                color: `${colors.radioGroup.disabledBorder} !important`
                                                            },
                                                            '&.Mui-checked.Mui-disabled': {
                                                                color: `${colors.radioGroup.disabledBorder} !important`
                                                            }
                                                        }}
                                                    />
                                                }
                                                label="Yes"
                                                disabled
                                            />

                                            <FormControlLabel
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        color: `${colors.radioGroup.optionText} !important`
                                                    },
                                                    '&.Mui-disabled .MuiFormControlLabel-label': {
                                                        color: `${colors.radioGroup.disabledBorder} !important`
                                                    }
                                                }}
                                                value="no"
                                                control={
                                                    <Radio
                                                        sx={{
                                                            color: `${colors.radioGroup.radioBorder} !important`,
                                                            '&.Mui-checked': {
                                                                color: `${colors.radioGroup.radioCheckedBackground} !important`
                                                            },
                                                            '&.Mui-disabled': {
                                                                color: `${colors.radioGroup.disabledBorder} !important`
                                                            },
                                                            '&.Mui-checked.Mui-disabled': {
                                                                color: `${colors.radioGroup.disabledBorder} !important`
                                                            }
                                                        }}
                                                    />
                                                }
                                                label="No"
                                                disabled
                                            />
                                        </RadioGroup>
                                    </Box>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Stack>
            <Stack>
                <Button
                    variant="pepwiseButtonText"
                    sx={{ width: '8rem', color: colors.buttons.primary.textColor }}
                    onClick={handleAddNewRow}
                    disabled={false} 
                >
                    + Add Hop
                </Button>
            </Stack>
            <DeleteHopConfirmationDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                hopName={selectedHopName}
            />
        </Box>
    );
}